/**
 * useEntityReferences
 *
 * Owns entity reference resolution for one message thread. Message bodies are
 * handed to `track`; every reference across them is collected and the ones
 * this thread has not asked about yet go out in a single batched request.
 *
 * A key is asked about exactly once. Keys the server did not return are
 * recorded as attempted so an unresolvable reference — or a failed request —
 * cannot drive a retry loop as the thread re-renders.
 *
 * Resolution enriches; it never gates. A failure leaves every pill plain.
 */

import { Accessor, createEffect, createSignal } from "solid-js";
import {
  resolveReferences,
  type DocumentReference,
  type WorkItemReference,
} from "../api/references";
import { displayIdKey, parseReferences } from "../utils/references";

export type ResolvedReference =
  | { kind: "work_item"; item: WorkItemReference }
  | { kind: "document"; doc: DocumentReference };

export interface UseEntityReferencesResult {
  /** Ask for these message bodies to be scanned and their references resolved. */
  track: (texts: string[]) => void;
  /** Look up a resolved reference by the pill's data-ref-key. Undefined until resolved (or if unresolvable). */
  lookup: (key: string) => ResolvedReference | undefined;
  /** Bumps whenever new resolutions land, so callers can re-run decoration. */
  version: Accessor<number>;
}

export function useEntityReferences(
  projectId: Accessor<string | undefined>,
  prefix: Accessor<string | undefined>,
): UseEntityReferencesResult {
  const [version, setVersion] = createSignal(0);
  let resolved: Record<string, ResolvedReference> = {};
  let attempted = new Set<string>();

  // A thread's cache is scoped to its project: the same display ID means a
  // different item under a different prefix, and the uuids are not visible
  // to a non-member of the new project either.
  createEffect(() => {
    projectId();
    resolved = {};
    attempted = new Set();
    setVersion((n) => n + 1);
  });

  const track = (texts: string[]): void => {
    const project = projectId();
    if (!project) return;
    // A missing prefix only disables display IDs — `createDisplayIdRegex`
    // returns null for an empty prefix. `/items/<uuid>` links need no prefix
    // and must still resolve.
    const projectPrefix = prefix() ?? "";

    const wanted = new Map<
      string,
      ReturnType<typeof parseReferences>[number]
    >();
    for (const text of texts) {
      for (const reference of parseReferences(text, projectPrefix)) {
        if (attempted.has(reference.key)) continue;
        if (!wanted.has(reference.key)) wanted.set(reference.key, reference);
      }
    }
    if (wanted.size === 0) return;

    const displayIds: string[] = [];
    const workItemIds: string[] = [];
    const documentIds: string[] = [];
    for (const reference of wanted.values()) {
      attempted.add(reference.key);
      if (reference.displayId) displayIds.push(reference.displayId);
      else if (reference.kind === "work_item" && reference.id)
        workItemIds.push(reference.id);
      else if (reference.id) documentIds.push(reference.id);
    }

    void resolveReferences(project, {
      work_item_display_ids: displayIds,
      work_item_ids: workItemIds,
      document_ids: documentIds,
    })
      .then((response) => {
        if (projectId() !== project) return;
        for (const item of response.work_items) {
          const entry: ResolvedReference = { kind: "work_item", item };
          resolved[`work_item:${item.id}`] = entry;
          resolved[displayIdKey(item.display_id)] = entry;
        }
        for (const doc of response.documents) {
          resolved[`document:${doc.id}`] = { kind: "document", doc };
        }
        setVersion((n) => n + 1);
      })
      .catch((error) => {
        // References stay plain. The keys are already marked attempted, so
        // this batch is not retried.
        console.warn("Failed to resolve chat references", error);
      });
  };

  const lookup = (key: string): ResolvedReference | undefined => resolved[key];

  return { track, lookup, version };
}
